import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as authApi from "@/api/auth";

type Step = "email" | "phone" | "verify" | "reset" | "done";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("email");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // 폼 데이터
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // 토큰
  const [verificationToken, setVerificationToken] = useState("");
  const [phoneVerifiedToken, setPhoneVerifiedToken] = useState("");

  const formatPhone = (value: string) => {
    const nums = value.replace(/\D/g, "").slice(0, 11);
    if (nums.length <= 3) return nums;
    if (nums.length <= 7) return `${nums.slice(0, 3)}-${nums.slice(3)}`;
    return `${nums.slice(0, 3)}-${nums.slice(3, 7)}-${nums.slice(7)}`;
  };

  // 1단계: 이메일 입력 → 2단계로
  const handleEmailNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setError("");
    setStep("phone");
  };

  // 2단계: 인증번호 발송
  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone) return;
    setIsLoading(true);
    setError("");
    try {
      const res = await authApi.phoneSend(phone.replace(/-/g, ""));
      setVerificationToken(res.verificationToken);
      setStep("verify");
    } catch (err: any) {
      setError(err?.response?.data?.message || "인증번호 발송에 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 3단계: 인증번호 확인
  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verificationCode) return;
    setIsLoading(true);
    setError("");
    try {
      const res = await authApi.phoneVerify(verificationToken, verificationCode);
      if (res.verified) {
        setPhoneVerifiedToken(res.phoneVerifiedToken);
        setStep("reset");
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || "인증번호가 올바르지 않습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 4단계: 새 비밀번호 설정
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== newPasswordConfirm) {
      setError("비밀번호가 일치하지 않습니다.");
      return;
    }
    if (newPassword.length < 8) {
      setError("비밀번호는 8자 이상이어야 합니다.");
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      await authApi.resetPassword(email, phoneVerifiedToken, newPassword);
      setStep("done");
    } catch (err: any) {
      setError(err?.response?.data?.message || "비밀번호 변경에 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  const stepConfig: Record<Step, { title: string; desc: string; num: number }> = {
    email: { title: "비밀번호 찾기", desc: "가입하신 이메일을 입력해주세요", num: 1 },
    phone: { title: "본인 인증", desc: "휴대폰 번호를 입력해주세요", num: 2 },
    verify: { title: "인증번호 확인", desc: "문자로 발송된 인증번호를 입력해주세요", num: 2 },
    reset: { title: "새 비밀번호 설정", desc: "새로운 비밀번호를 입력해주세요", num: 3 },
    done: { title: "변경 완료", desc: "비밀번호가 성공적으로 변경되었습니다", num: 3 },
  };

  const current = stepConfig[step];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF8F0] via-white to-[#FFF0E6] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="font-serif text-4xl font-medium tracking-tight text-[#2D2D2D]">봄날</span>
            <span className="text-sm tracking-[0.2em] font-light text-[#C9A96E]">AI WEDDING</span>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-10">
          {/* 스텝 인디케이터 */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2, 3].map((n) => (
              <div key={n} className="flex items-center gap-2">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                    n <= current.num
                      ? "bg-[#C9A96E] text-white"
                      : "bg-gray-100 text-gray-400"
                  }`}
                >
                  {n}
                </div>
                {n < 3 && (
                  <div className={`w-8 h-0.5 ${n < current.num ? "bg-[#C9A96E]" : "bg-gray-200"}`}></div>
                )}
              </div>
            ))}
          </div>

          <h2 className="text-xl font-medium text-gray-800 text-center mb-2">{current.title}</h2>
          <p className="text-sm text-gray-500 text-center mb-8">{current.desc}</p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-6">
              {error}
            </div>
          )}

          {/* 이메일 입력 */}
          {step === "email" && (
            <form onSubmit={handleEmailNext} className="space-y-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 font-medium">이메일</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="가입하신 이메일을 입력해주세요"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#C9A96E] focus:ring-1 focus:ring-[#C9A96E] transition-colors"
                />
              </div>
              <button
                type="submit"
                className="w-full py-3.5 bg-[#C9A96E] text-white font-medium rounded-xl hover:bg-[#B8956A] transition-all duration-300 shadow-md hover:shadow-lg cursor-pointer"
              >
                다음
              </button>
            </form>
          )}

          {/* 휴대폰 번호 입력 */}
          {step === "phone" && (
            <form onSubmit={handleSendCode} className="space-y-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 font-medium">휴대폰 번호</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(formatPhone(e.target.value))}
                  placeholder="010-0000-0000"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#C9A96E] focus:ring-1 focus:ring-[#C9A96E] transition-colors"
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3.5 bg-[#C9A96E] text-white font-medium rounded-xl hover:bg-[#B8956A] transition-all duration-300 shadow-md hover:shadow-lg cursor-pointer disabled:opacity-50"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    발송 중...
                  </span>
                ) : (
                  "인증번호 받기"
                )}
              </button>
            </form>
          )}

          {/* 인증번호 확인 */}
          {step === "verify" && (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 font-medium">인증번호</label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="인증번호 입력"
                  required
                  maxLength={6}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm text-center tracking-[0.5em] font-medium focus:outline-none focus:border-[#C9A96E] focus:ring-1 focus:ring-[#C9A96E] transition-colors"
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3.5 bg-[#C9A96E] text-white font-medium rounded-xl hover:bg-[#B8956A] transition-all duration-300 shadow-md hover:shadow-lg cursor-pointer disabled:opacity-50"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    확인 중...
                  </span>
                ) : (
                  "확인"
                )}
              </button>
              <button
                type="button"
                onClick={() => { setStep("phone"); setVerificationCode(""); setError(""); }}
                className="w-full py-2 text-sm text-gray-400 hover:text-[#C9A96E] transition-colors cursor-pointer"
              >
                인증번호 다시 받기
              </button>
            </form>
          )}

          {/* 새 비밀번호 */}
          {step === "reset" && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 font-medium">새 비밀번호</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="8자 이상 입력해주세요"
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#C9A96E] focus:ring-1 focus:ring-[#C9A96E] transition-colors pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 font-medium">새 비밀번호 확인</label>
                <input
                  type="password"
                  value={newPasswordConfirm}
                  onChange={(e) => setNewPasswordConfirm(e.target.value)}
                  placeholder="비밀번호를 다시 입력해주세요"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#C9A96E] focus:ring-1 focus:ring-[#C9A96E] transition-colors"
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3.5 bg-[#C9A96E] text-white font-medium rounded-xl hover:bg-[#B8956A] transition-all duration-300 shadow-md hover:shadow-lg cursor-pointer disabled:opacity-50"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    변경 중...
                  </span>
                ) : (
                  "비밀번호 변경"
                )}
              </button>
            </form>
          )}

          {/* 완료 */}
          {step === "done" && (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-green-50 rounded-full flex items-center justify-center">
                <i className="ri-check-line text-3xl text-green-500"></i>
              </div>
              <p className="text-sm text-gray-600 mb-8">
                새로운 비밀번호로 로그인해주세요.
              </p>
              <button
                onClick={() => navigate("/login", { replace: true })}
                className="w-full py-3.5 bg-[#C9A96E] text-white font-medium rounded-xl hover:bg-[#B8956A] transition-all duration-300 shadow-md hover:shadow-lg cursor-pointer"
              >
                로그인하러 가기
              </button>
            </div>
          )}
        </div>

        <div className="mt-8 text-center">
          <Link to="/login" className="text-sm text-gray-500 hover:text-[#C9A96E] transition-colors duration-300 inline-flex items-center gap-2">
            <i className="ri-arrow-left-line"></i>
            <span>로그인으로 돌아가기</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
